import React, { useState, useEffect } from "react";
import { 
  listHorariosGenerales, 
  createHorarioGeneral, 
  updateHorarioGeneral 
} from "../api/odontologia";
import { showAlertError } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiPlus, FiClock, FiCalendar, FiEdit3, FiTrash2, FiInfo
} from "react-icons/fi";

const HorariosOdontologia = () => {
  const [loading, setLoading] = useState(true);
  const [horarios, setHorarios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const diasSemana = [
    { id: 1, name: "Lunes" },
    { id: 2, name: "Martes" },
    { id: 3, name: "Miércoles" },
    { id: 4, name: "Jueves" },
    { id: 5, name: "Viernes" },
    { id: 6, name: "Sábado" },
    { id: 7, name: "Domingo" }
  ];

  const [form, setForm] = useState({
    dia_semana: 1,
    hora_inicio: "08:00",
    hora_fin: "12:00",
    status: 1
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listHorariosGenerales();
      setHorarios(res?.data || []);
    } catch (error) {
      console.error("Error al cargar horarios:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (horario = null) => {
    if (horario) {
      setForm({
        dia_semana: horario.dia_semana,
        hora_inicio: horario.hora_inicio.substring(0, 5),
        hora_fin: horario.hora_fin.substring(0, 5),
        status: horario.status !== undefined ? horario.status : 1
      });
      setCurrentId(horario.id);
      setEditMode(true);
    } else {
      setForm({
        dia_semana: 1,
        hora_inicio: "08:00",
        hora_fin: "12:00",
        status: 1
      });
      setEditMode(false);
    }
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dataToSend = {
        ...form,
        dia_semana: parseInt(form.dia_semana),
        hora_inicio: form.hora_inicio.length === 5 ? `${form.hora_inicio}:00` : form.hora_inicio,
        hora_fin: form.hora_fin.length === 5 ? `${form.hora_fin}:00` : form.hora_fin,
        status: parseInt(form.status)
      };

      if (editMode) {
        await updateHorarioGeneral(currentId, dataToSend);
      } else {
        await createHorarioGeneral(dataToSend);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error("Error al guardar horario:", error);
      showAlertError("Error", "No se pudo guardar el horario.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && horarios.length === 0) return <Loader />;

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto font-sans">
      
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">Horario General</h2>
          <p className="text-sm text-slate-500 mt-1">Definición de turnos de atención de la clínica por día de la semana</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="w-full sm:w-auto bg-indigo-600 text-white px-5 py-3 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 font-semibold flex items-center justify-center gap-2"
        >
          <FiPlus size={20} /> Nuevo Segmento
        </button>
      </div>

      {/* Grid de días */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {diasSemana.map((dia) => {
          const horariosDia = horarios.filter(h => h.dia_semana === dia.id).sort((a,b) => a.hora_inicio.localeCompare(b.hora_inicio));
          
          return (
            <div key={dia.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <span className="font-bold text-slate-700 uppercase tracking-wider text-xs">{dia.name}</span>
                <span className="bg-indigo-100 text-indigo-600 font-bold px-2 py-0.5 rounded text-[10px]">Día {dia.id}</span>
              </div>
              
              <div className="p-4 flex-grow space-y-3">
                {horariosDia.length > 0 ? (
                  horariosDia.map((h) => (
                    <div key={h.id} className="group relative flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 transition-all">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                          <FiClock size={14} className="text-indigo-500" />
                          {h.hora_inicio.substring(0, 5)} - {h.hora_fin.substring(0, 5)}
                        </div>
                        <span className={`mt-1 inline-block w-fit px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${h.status === 1 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                          {h.status === 1 ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <button 
                        onClick={() => handleOpenModal(h)}
                        className="opacity-0 group-hover:opacity-100 p-2 text-slate-400 hover:text-indigo-600 transition-all bg-white rounded-lg shadow-sm border border-slate-200"
                      >
                        <FiEdit3 size={14} />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-300 italic">
                    <FiCalendar size={32} className="mb-2 opacity-20" />
                    <p className="text-xs">Sin horarios</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md animate-fade-in-up">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-inner">
                  <FiClock size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">
                    {editMode ? "Editar Horario" : "Nuevo Horario"}
                  </h3>
                  <p className="text-xs text-slate-500">Define el segmento de atención</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors">
                <FiPlus size={24} className="rotate-45" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block mb-2 text-sm font-bold text-slate-700">Día de la Semana</label>
                <select
                  name="dia_semana"
                  value={form.dia_semana}
                  onChange={handleChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm outline-none"
                  required
                >
                  {diasSemana.map(d => (
                    <option key={d.id} value={d.id}>{d.name} (Día {d.id})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-bold text-slate-700">Hora Inicio</label>
                  <input 
                    type="time" 
                    name="hora_inicio"
                    value={form.hora_inicio}
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-bold text-slate-700">Hora Fin</label>
                  <input 
                    type="time" 
                    name="hora_fin"
                    value={form.hora_fin}
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all text-sm outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-bold text-slate-700">Estado</label>
                <div className="flex gap-4">
                  <label className="flex-1 cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="1" 
                      checked={form.status == 1}
                      onChange={handleChange}
                      className="peer hidden" 
                    />
                    <div className="p-3 text-center rounded-xl border-2 border-slate-100 bg-slate-50 peer-checked:bg-emerald-50 peer-checked:border-emerald-500 peer-checked:text-emerald-700 transition-all text-xs font-bold text-slate-400">
                      ACTIVO
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="0" 
                      checked={form.status == 0}
                      onChange={handleChange}
                      className="peer hidden" 
                    />
                    <div className="p-3 text-center rounded-xl border-2 border-slate-100 bg-slate-50 peer-checked:bg-rose-50 peer-checked:border-rose-500 peer-checked:text-rose-700 transition-all text-xs font-bold text-slate-400">
                      INACTIVO
                    </div>
                  </label>
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-slate-100 text-slate-500 font-bold hover:bg-slate-50 transition-all text-sm"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-[2] px-6 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 text-sm flex items-center justify-center gap-2"
                >
                  {submitting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                  {editMode ? "Actualizar" : "Guardar Horario"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default HorariosOdontologia;
